/** Phase 3B-2b — Calibration Coaching card.
 *
 * Surfaces the single most out-of-calibration confidence bucket for the current
 * workspace. Purely informational — never mutates raw confidence or sizing.
 *
 * Safeguards:
 *   - Requires ≥ 20 completed outcomes in the bucket
 *   - Requires absolute calibration gap ≥ 10 percentage points
 *   - Respects local snooze/dismiss state (7-day snooze, permanent dismiss until
 *     new evidence tips the bucket into a different range)
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Info, X, Clock, Activity, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";

const MIN_BUCKET_SAMPLE = 20;
const MIN_ABS_GAP_PP = 10;
const PROMINENT_GAP_PP = 20;
const SNOOZE_KEY = "tm_calibration_coach_snooze";  // stores { bucket, until_iso }
const DISMISS_KEY = "tm_calibration_coach_dismiss"; // stores { bucket, gap }

function _readState(key) {
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; }
}
function _writeState(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
  window.dispatchEvent(new Event("tm:calibration-coach-changed"));
}

function _bestFinding(cal) {
  if (!cal) return null;
  const candidates = (cal.buckets || [])
    .filter((b) => b.completed_outcome_count >= MIN_BUCKET_SAMPLE)
    .filter((b) => b.calibration_gap_pct != null && Math.abs(b.calibration_gap_pct) >= MIN_ABS_GAP_PP)
    .sort((a, b) => Math.abs(b.calibration_gap_pct) - Math.abs(a.calibration_gap_pct));
  return candidates[0] || null;
}

export default function CalibrationCoachCard({ compact = false, testId = "calibration-coach-card" }) {
  const nav = useNavigate();
  const { data: cal } = useQuery({
    queryKey: qk(["performance", "calibration"], { filters: { outcome_rule: "net_positive_return" } }),
    queryFn: async () => (await api.get("/performance/calibration")).data,
    staleTime: STALE.SHORT,
    gcTime: GC.SHORT,
  });

  const finding = useMemo(() => _bestFinding(cal), [cal]);
  const snooze = _readState(SNOOZE_KEY);
  const dismiss = _readState(DISMISS_KEY);

  if (!finding) return null;

  // Respect snooze if it targets the same bucket and hasn't expired.
  if (snooze && snooze.bucket === finding.range && new Date(snooze.until_iso) > new Date()) return null;

  // Respect dismiss unless the finding has clearly shifted (bucket or gap).
  if (dismiss && dismiss.bucket === finding.range && Math.abs(dismiss.gap - finding.calibration_gap_pct) < 3) return null;

  const overconfident = finding.calibration_gap_pct > 0;
  const gap = Math.abs(finding.calibration_gap_pct);
  const prominent = gap >= PROMINENT_GAP_PP;
  const evidence_tier = finding.completed_outcome_count >= 50 ? "stronger evidence"
                       : finding.completed_outcome_count >= 30 ? "moderate"
                       : "developing";

  const suggested = overconfident
    ? Math.max(50, Math.round((finding.actual_success_rate_pct || 0) / 5) * 5)
    : Math.min(95, Math.round((finding.actual_success_rate_pct || 0) / 5) * 5);

  const onSnooze = () => {
    _writeState(SNOOZE_KEY, { bucket: finding.range, until_iso: new Date(Date.now() + 7 * 86400_000).toISOString() });
  };
  const onDismiss = () => {
    _writeState(DISMISS_KEY, { bucket: finding.range, gap: finding.calibration_gap_pct });
  };

  const bg = prominent
    ? (overconfident ? "border-signal-red/40 bg-signal-red/5" : "border-signal-yellow/40 bg-signal-yellow/5")
    : "border-border/60 bg-secondary/20";

  return (
    <div className={`grid-cell !p-3 ${bg} space-y-2`} role="status" aria-live="polite" data-testid={testId}>
      <div className="flex items-start gap-2">
        <Activity size={14} className={overconfident ? "pnl-down shrink-0 mt-0.5" : "text-signal-yellow shrink-0 mt-0.5"} aria-hidden="true"/>
        <div className="flex-1 min-w-0">
          <div className="label-cap flex items-center gap-2">
            <span>Confidence Calibration</span>
            {prominent && <span className={`chip !text-[9px] uppercase border-current ${overconfident ? "pnl-down" : "text-signal-yellow"}`}>Attention</span>}
          </div>
          {compact ? (
            <div className="text-xs mt-1">
              Your <span className="mono">{finding.range}</span> confidence trades win{" "}
              <span className={overconfident ? "pnl-down mono" : "pnl-up mono"}>{finding.actual_success_rate_pct}%</span> of the time.
              Treat as ~<span className="mono">{suggested}%</span> signals.
            </div>
          ) : (
            <div className="text-xs mt-1 space-y-1">
              <div>
                Recommendations rated <span className="mono">{finding.range}%</span> confidence have historically succeeded{" "}
                <span className={overconfident ? "pnl-down mono" : "pnl-up mono"}>{finding.actual_success_rate_pct}%</span> of the time.
                {overconfident
                  ? " The model appears overconfident in this range."
                  : " The model may be underconfident in this range."}
              </div>
              <div className="text-muted-foreground">
                Suggested interpretation: treat these as approximately <span className="mono text-foreground">{suggested}%</span> confidence signals until more evidence becomes available.
              </div>
              <ul className="text-[10px] mono uppercase text-muted-foreground mt-1 space-y-0.5" data-testid="coach-evidence">
                <li>• Completed outcomes: {finding.completed_outcome_count}</li>
                <li>• Average raw confidence: {finding.predicted_avg_confidence}%</li>
                <li>• Actual success rate: {finding.actual_success_rate_pct}%</li>
                <li>• Calibration gap: {finding.calibration_gap_pct > 0 ? "+" : ""}{finding.calibration_gap_pct} pp</li>
                <li>• Evidence tier: {evidence_tier}</li>
              </ul>
              <div className="text-[10px] mono uppercase text-muted-foreground pt-1">
                Informational only — raw AI confidence and position sizing are unchanged.
              </div>
            </div>
          )}
        </div>
        <button onClick={onDismiss} className="text-muted-foreground/70 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" aria-label="Dismiss coaching card" data-testid="coach-dismiss">
          <X size={12}/>
        </button>
      </div>
      {!compact && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <button onClick={() => nav("/performance?tab=calibration")} className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" data-testid="coach-view-calibration">
            <ExternalLink size={10} className="inline mr-1"/>View Calibration
          </button>
          <button onClick={() => nav("/performance?tab=attribution")} className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" data-testid="coach-review-trades">
            <ExternalLink size={10} className="inline mr-1"/>Review Trades
          </button>
          <button onClick={onSnooze} className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" data-testid="coach-snooze">
            <Clock size={10} className="inline mr-1"/>Snooze 7d
          </button>
        </div>
      )}
    </div>
  );
}
